import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import { Controller } from 'react-hook-form';

import Alert from '@/core/components/Alert';
import Button from '@/core/components/Button';
import FormContainer from '@/core/components/FormContainer';
import Header from '@/core/components/Header';
import HintMessage from '@/core/components/HintMessage';
import CheckboxInput from '@/core/components/inputs/CheckboxInput';
import CityInput from '@/core/components/inputs/CityInput';
import PasswordInput from '@/core/components/inputs/PasswordInput';
import NewTextInput from '@/core/components/inputs/TextInput/NewTextInput';
import Label from '@/core/components/Label';
import useForm from '@/core/hooks/useForm';
import { AnalyticsEventType, useAnalyticsContext } from '@/core/lib/analytics/analytics.context';
import { useAuthContext } from '@/core/lib/auth/auth.context';
import { useEnvContext } from '@/core/lib/env/env.context';
import { ApiMethods } from '@/core/lib/fetch';
import { ApiVersions } from '@/core/lib/fetch/fetch';
import Actions from '@/core/lib/new-architecture/actions';
import { useRouterContext } from '@/core/lib/router/router.context';
import Routes from '@/core/lib/router/routes';
import { useTranslationContext } from '@/core/lib/translation/translation.context';
import { useAuthRoutesContext } from '@/core/pages/auth/Authentification';
import type { PageWithLayoutsAndProviders } from '@/core/pages/page.types';
import type { UsersAuthRegisterForm, UsersAuthResult } from '@/core/types/auth';
import { usersAuthRegisterFormSchema } from '@/core/types/auth';

const Register: PageWithLayoutsAndProviders = () => {
  const { DONNONS_API_URL } = useEnvContext();
  const { t } = useTranslationContext(['auth.register', 'common']);
  const { back, onClose, authRegisterFormValues } = useAuthRoutesContext();
  const { login } = useAuthContext();
  const { send, getUtmOrigin } = useAnalyticsContext();
  const { Link } = useRouterContext();

  const { onSubmit: patchNewsletter } = Actions.user.usePatchNewsletter({ onSuccess: () => {} });

  const { control, onSubmit, errors, onCleanGlobalError, isLoading, setFocus, getValues, setValue } = useForm<UsersAuthResult, UsersAuthRegisterForm>({
    schema: usersAuthRegisterFormSchema,
    apiDefinition: { method: ApiMethods.POST, url: `${DONNONS_API_URL}${ApiVersions.V1}/signup` },
    transformers: {
      formToApi: form => ({ ...form, loc: form.location.loc, origin: getUtmOrigin() }),
    },
    onSuccess: async data => {
      await login({
        accessToken: data.access_token,
        accessExpiresAt: dayjs().add(data.access_expires_in, 'second'),
        refreshToken: data.refresh_token ?? null,
        refreshExpiresAt: data.refresh_expires_in ? dayjs().add(data.refresh_expires_in, 'second') : null,
      });

      await patchNewsletter({ push: getValues('push'), newsletter: getValues('newsletter') });
      await send({ event: AnalyticsEventType.SIGN_UP_COMPLETE, origin: 'email' });
      onClose({ success: true });
    },
  });

  useEffect(() => {
    if (authRegisterFormValues) {
      Object.entries(authRegisterFormValues).forEach(([key, value]) => {
        setValue(key as keyof UsersAuthRegisterForm, value);
      });
    }
  }, [authRegisterFormValues]);

  return (
    <div className="flex h-full flex-col gap-4 md:gap-7">
      <Header content={t('title', { ns: 'auth.register' })} iconBefore="arrow-left" onBefore={back} />
      <div className="inline-flex h-full flex-col pt-3">
        {errors.global.isError && errors.global.message && (
          <Alert status="error" onClose={onCleanGlobalError}>
            {t(errors.global.message, { ns: 'common' })} {errors.global.code}
          </Alert>
        )}

        <FormContainer>
          <Controller
            control={control}
            defaultValue=""
            render={({ field: { ref, onChange, onBlur, value } }) => {
              const { isError, message, isSuccess } = errors.username;
              return (
                <Label htmlFor="username" content={t('form.username.label', { ns: 'auth.register' })}>
                  <NewTextInput
                    id="username"
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value}
                    placeholder={t('form.username.placeholder', { ns: 'auth.register' })}
                    autoComplete="username"
                    inputMode="text"
                    validation={{ isError, isSuccess }}
                    ref={ref}
                    enterKeyHint="next"
                    handleOnKeyDown={e => {
                      if (e.key === 'Enter') {
                        setFocus('email');
                      }
                    }}
                  />
                  {!isError && <HintMessage message={t('inputs.username.hint', { ns: 'common' })} />}
                  {isError && <HintMessage isError={isError} isSuccess={isSuccess} message={message} />}
                </Label>
              );
            }}
            name="username"
          />

          <Controller
            control={control}
            defaultValue=""
            render={({ field: { ref, onChange, onBlur, value } }) => {
              const { isError, message, isSuccess } = errors.email;
              return (
                <Label htmlFor="email" content={t('form.email.label', { ns: 'auth.register' })}>
                  <NewTextInput
                    id="email"
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value}
                    placeholder={t('form.email.placeholder', { ns: 'auth.register' })}
                    autoComplete="email"
                    inputMode="email"
                    validation={{ isError, isSuccess }}
                    ref={ref}
                    enterKeyHint="next"
                    handleOnKeyDown={e => {
                      if (e.key === 'Enter') {
                        setFocus('password');
                      }
                    }}
                  />
                  <HintMessage isError={isError} isSuccess={isSuccess} message={message} />
                </Label>
              );
            }}
            name="email"
          />

          <Controller
            control={control}
            defaultValue=""
            render={({ field: { ref, onChange, onBlur } }) => {
              const { isError, message, isSuccess } = errors.password;
              return (
                <Label htmlFor="password" content={t('form.password.label', { ns: 'auth.register' })}>
                  <PasswordInput
                    id="password"
                    onChange={onChange}
                    onBlur={onBlur}
                    placeholder={t('form.password.placeholder', { ns: 'auth.register' })}
                    autocomplete="new-password"
                    validation={{ isError, isSuccess }}
                    ref={ref}
                    enterKeyHint="next"
                    handleOnKeyDown={e => {
                      if (e.key === 'Enter') {
                        setFocus('location');
                      }
                    }}
                  />
                  {!isError && <HintMessage message={t('inputs.password.hint', { ns: 'common' })} />}
                  {isError && <HintMessage isError={isError} isSuccess={isSuccess} message={message} />}
                </Label>
              );
            }}
            name="password"
          />

          <Controller
            control={control}
            render={({ field: { ref, onChange, onBlur, value } }) => {
              const { isError, message, isSuccess } = errors.location;
              return (
                <Label htmlFor="location" content={t('form.location.label', { ns: 'auth.register' })}>
                  <CityInput onChange={onChange} onBlur={onBlur} id="location" value={value} placeholder={t('form.location.placeholder', { ns: 'auth.register' })} ref={ref} cityInputType="limited" />
                  <HintMessage isError={isError} isSuccess={isSuccess} message={message} />
                </Label>
              );
            }}
            name="location"
          />

          <div className="py-3">
            <Controller
              control={control}
              defaultValue={false}
              render={({ field: { onChange, onBlur, value } }) => {
                const { isError, message, isSuccess } = errors.cgu;
                return (
                  <>
                    <CheckboxInput onChange={onChange} onBlur={onBlur} id="cgu" value={value} validation={{ isError, isSuccess }}>
                      {t('form.cgu.label', { ns: 'auth.register' })}&nbsp;
                      <Link href={new Routes.CguRoute()} target="_blank" isInAppBrowser className="text-body-secondary text-wrap !p-0 text-content-secondary underline">
                        {t('form.cgu.cgu-and-privacy', { ns: 'auth.register' })}
                      </Link>
                    </CheckboxInput>
                    {isError && <HintMessage isError={isError} isSuccess={isSuccess} message={message} />}
                  </>
                );
              }}
              name="cgu"
            />

            <Controller
              control={control}
              defaultValue={false}
              render={({ field: { onChange, onBlur, value } }) => {
                const { isError, isSuccess } = errors.newsletter;
                return (
                  <CheckboxInput onChange={onChange} onBlur={onBlur} id="newsletter" value={value} validation={{ isError, isSuccess }}>
                    {t('form.newsletter.label', { ns: 'auth.register' })}
                  </CheckboxInput>
                );
              }}
              name="newsletter"
            />
            <Controller
              control={control}
              defaultValue={false}
              render={({ field: { onChange, onBlur, value } }) => {
                const { isError, isSuccess } = errors.newsletter;
                return (
                  <CheckboxInput onChange={onChange} onBlur={onBlur} id="push" value={value} validation={{ isError, isSuccess }}>
                    {t('form.push.label', { ns: 'auth.register' })}
                  </CheckboxInput>
                );
              }}
              name="push"
            />
          </div>
        </FormContainer>

        <div className="mt-auto flex w-full justify-end">
          <div className="max-w-max">
            <Button type="submit" isLoading={isLoading} onClick={onSubmit}>
              {t('form.submit', { ns: 'auth.register' })}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
